// Verbatim TypeScript port of the v9.1 Dot Studio render pipeline
// (morfi-pattern-studio_9_1.html). This is the ONLY renderer for generative
// covers: grid → dots → final coloured canvas. Colours come straight from the
// HTML's pickColor + DOT_PALETTES — no mosaic, no brand-blue override layer.

import { cellRng, clamp, hashSeed, mulberry32, sdTri, smoothstep } from './math';
import { pickColor } from './palettes';
import type { DensityFieldConfig, DerivedSeed } from './types';

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Alpha mask sampled by the hidden brand-mark locus (`morfi` markShape). */
export interface MarkMask {
  readonly w: number;
  readonly h: number;
  readonly data: Uint8Array | Uint8ClampedArray;
}

export type GridType = 'square' | 'hex' | 'radial';
export type FadeMode = 'none' | 'edge' | 'vignette';
export type MarkVariant = 'axis' | 'diag' | 'mix';

/** Full engine config consumed by buildDots + drawCanvas. */
export interface DotRenderConfig extends DensityFieldConfig {
  readonly spacing: number;
  readonly minFrac: number;
  readonly maxFrac: number;
  readonly intensity: number;
  readonly shape: string;
  readonly seamless: boolean;
  readonly shapePow: number;
  readonly coverage: number;
  readonly baseFill: number;
  readonly gridType: GridType;
  readonly gridAngle: number;
  readonly mark: string;
  readonly markShape: string;
  readonly markLayout: string;
  readonly markPolarity: string;
  readonly markSize: number;
  readonly markThick: number;
  readonly markStrength: number;
  readonly markMask?: MarkMask | null;
  readonly markVariant: MarkVariant;
  readonly colors: readonly [string, string, string, string];
  readonly background: string;
  readonly backgroundMode: string;
  readonly fadeMode: FadeMode;
  readonly fadeSize: number;
  readonly fadeRoughness: number;
  readonly shine: boolean;
  readonly shineAngle: number;
  readonly strokeWeight?: number;
  readonly jitterDots?: boolean;
  readonly colorAt?: ((x: number, y: number) => string) | null;
}

export interface Dot {
  x: number;
  y: number;
  r: number;
  color: string;
  alpha: number;
  shape: string;
  sw: number;
  tr: number;
  mk?: { variant: MarkVariant };
}

interface Locus {
  cx: number;
  cy: number;
  baseR: number;
  halfBand: number;
  rotation: number;
  inner: number;
  outer: number;
  sign: number;
  strength: number;
}

/* ---- hidden brand mark ---- */
function buildLoci(o: DotRenderConfig): Locus[] | null {
  if (!o.mark || o.mark === 'off') return null;
  const {
    width,
    height,
    markLayout: layout,
    markSize: size,
    markThick: thick,
    markStrength: strength,
    markPolarity: pol,
    seed,
    derived,
  } = o;
  const minSide = Math.min(width, height);
  const gsign = pol === 'negative' ? -1 : 1;
  const TRIO: ReadonlyArray<readonly [number, number, number]> = [
    [0.5, 0.52, 1],
    [0.18, 0.18, 0.62],
    [0.82, 0.8, 0.58],
  ];
  const fixed: Record<string, ReadonlyArray<readonly [number, number, number]>> = {
    single: [[0.5, 0.5, 1]],
    dual: [
      [0.3, 0.34, 0.78],
      [0.7, 0.66, 0.78],
    ],
    trio: TRIO,
  };
  let positions: ReadonlyArray<readonly [number, number, number]>;
  let signs: number[];
  if (layout === 'scatter') {
    const rng = mulberry32((seed ^ 0xa5a5) >>> 0);
    const n = derived?.outcomes ? derived.outcomes + 2 : 5;
    const pos: Array<readonly [number, number, number]> = [];
    signs = [];
    for (let i = 0; i < n; i++) {
      pos.push([0.1 + rng() * 0.8, 0.12 + rng() * 0.76, 0.2 + rng() * 1.0]);
      signs.push(rng() < 0.75 ? gsign : -gsign);
    }
    positions = pos;
  } else {
    positions = fixed[layout] ?? TRIO;
    signs = positions.map(() => gsign);
  }
  return positions.map(([px, py, sc], i) => {
    const cx = width * px;
    const cy = height * py;
    const baseR = minSide * 0.42 * size * sc;
    const halfBand = Math.max(2, baseR * thick * 0.5);
    const rotation = ((hashSeed(`${seed}|rot|${i}`) % 3600) / 3600) * Math.PI * 2;
    return {
      cx,
      cy,
      baseR,
      halfBand,
      rotation,
      inner: Math.max(1, baseR - halfBand),
      outer: Math.max(2, baseR + halfBand),
      sign: signs[i] ?? gsign,
      strength,
    };
  });
}

function lociBand(
  o: Locus,
  rx: number,
  ry: number,
  shape: string,
  mask: MarkMask | null | undefined,
): number {
  if (shape === 'morfi' && mask) {
    const u = (rx - o.cx) / (o.baseR * 2) + 0.5;
    const v = (ry - o.cy) / (o.baseR * 2) + 0.5;
    if (u < 0 || u >= 1 || v < 0 || v >= 1) return 0;
    const ix = Math.min(mask.w - 1, (u * mask.w) | 0);
    const iy = Math.min(mask.h - 1, (v * mask.h) | 0);
    return (mask.data[iy * mask.w + ix] ?? 0) / 255;
  }
  if (shape === 'triangle') {
    const dx = rx - o.cx;
    const dy = ry - o.cy;
    const c = Math.cos(-o.rotation);
    const s = Math.sin(-o.rotation);
    const px = dx * c - dy * s;
    const py = dx * s + dy * c;
    const ad = Math.abs(sdTri(px, py, o.baseR));
    return 1 - smoothstep(o.halfBand * 0.55, o.halfBand * 1.15, ad);
  }
  const dd = Math.hypot(rx - o.cx, ry - o.cy);
  return smoothstep(o.inner * 0.85, o.inner, dd) * (1 - smoothstep(o.outer, o.outer * 1.1, dd));
}

/* ---- fade ---- */
function edgeNoise(x: number, y: number, seed: number, r: number): number {
  const sc = 0.004 + r * 0.008;
  return (
    (Math.sin(x * sc * 1.9 + seed * 0.00013) * 0.45 +
      Math.cos(y * sc * 2.3 - seed * 0.00017) * 0.35 +
      Math.sin((x + y) * sc * 1.1 + seed * 0.00007) * 0.2 +
      1) *
    0.5
  );
}

function fadeMul(x: number, y: number, o: DotRenderConfig): number {
  if (o.fadeMode === 'none') return 1;
  const fpx = Math.max(1, Math.min(o.width, o.height) * o.fadeSize);
  const rough = clamp(o.fadeRoughness ?? 0.42, 0, 1);
  const off = (edgeNoise(x, y, o.seed, rough) - 0.5) * fpx * rough * 1.8;
  if (o.fadeMode === 'edge') {
    const ed = Math.min(x, y, o.width - x, o.height - y);
    return clamp(smoothstep(0, fpx, ed + off), 0, 1);
  }
  const cx = o.width * 0.5;
  const cy = o.height * 0.52;
  const dx = (x - cx) / (o.width * 0.5);
  const dy = (y - cy) / (o.height * 0.5);
  const dd = Math.hypot(dx, dy);
  return clamp(
    1 - smoothstep(1 - o.fadeSize * 2.2, 1.08, dd + off / Math.min(o.width, o.height)),
    0,
    1,
  );
}

/* ---- element shape resolution (shared by SVG + canvas so preview == export) ---- */
function resolveShape(shape: string, x: number, y: number): string {
  if (shape !== 'mixed') return shape;
  const p = (Math.sin(x * 0.13) + Math.cos(y * 0.09)) * 0.5 + 0.5;
  return p < 0.42 ? 'circle' : p < 0.74 ? 'line' : 'dash';
}

/* rounded regular triangle vertices (circumradius R, centroid cx,cy); up or down */
function triVerts(cx: number, cy: number, R: number, up: boolean): Array<{ x: number; y: number }> {
  return up
    ? [
        { x: cx, y: cy - R },
        { x: cx - R * 0.866, y: cy + R * 0.5 },
        { x: cx + R * 0.866, y: cy + R * 0.5 },
      ]
    : [
        { x: cx, y: cy + R },
        { x: cx + R * 0.866, y: cy - R * 0.5 },
        { x: cx - R * 0.866, y: cy - R * 0.5 },
      ];
}

/* The two canonical brand-mark variants (exact vector paths, 40x40 box centred at 20,20) */
const MARK_PATHS: Record<'axis' | 'diag', string> = {
  axis: 'M18.1827 23.2469C18.9379 21.7599 21.062 21.7599 21.8172 23.2469L28.8221 37.0388C29.5108 38.3947 28.5256 40 27.0048 40H12.995C11.4743 40 10.4891 38.3947 11.1778 37.0388L18.1827 23.2469Z M23.2468 21.8179C21.7598 21.0627 21.7598 18.9387 23.2468 18.1834L37.0388 11.1786C38.3947 10.4899 40 11.475 40 12.9958L40 27.0055C40 28.5263 38.3947 29.5115 37.0388 28.8228L23.2468 21.8179Z M21.8167 16.7531C21.0615 18.2401 18.9374 18.2401 18.1822 16.7531L11.1773 2.96119C10.4886 1.60527 11.4738 0 12.9945 0L27.0043 0C28.5251 0 29.5103 1.60527 28.8216 2.96119L21.8167 16.7531Z M16.7532 18.1828C18.2402 18.938 18.2402 21.0621 16.7532 21.8173L2.96121 28.8222C1.60528 29.5108 0 28.5257 0 27.0049L0 12.9952C0 11.4744 1.60528 10.4893 2.9612 11.178L16.7532 18.1828Z',
  diag: 'M16.3635 21.0266C17.9737 20.5012 19.499 22.0264 18.9735 23.6366L14.0995 38.5709C13.6204 40.0391 11.7601 40.4844 10.668 39.3923L0.607225 29.3319C-0.484892 28.2398 -0.0395612 26.3796 1.42871 25.9005L16.3635 21.0266Z M21.0263 23.637C20.5008 22.0269 22.0261 20.5016 23.6363 21.0271L38.5711 25.9009C40.0394 26.3801 40.4847 28.2403 39.3926 29.3323L29.3318 39.3928C28.2397 40.4849 26.3794 40.0396 25.9003 38.5713L21.0263 23.637Z M23.6365 18.9729C22.0263 19.4984 20.501 17.9731 21.0265 16.363L25.9005 1.42867C26.3796 -0.0395569 28.2399 -0.484877 29.332 0.607204L39.3928 10.6676C40.4849 11.7597 40.0396 13.6199 38.5713 14.0991L23.6365 18.9729Z M18.9737 16.3635C19.4992 17.9736 17.9739 19.4989 16.3637 18.9735L1.42891 14.0996C-0.039359 13.6205 -0.484693 11.7603 0.607423 10.6682L10.6682 0.60776C11.7603 -0.484321 13.6206 -0.039005 14.0997 1.42922L18.9737 16.3635Z',
};

const MARK_P2D: Partial<Record<'axis' | 'diag', Path2D>> = {};

/* ---- grid cell centers: square (optionally angled), hex packing, or radial rings ---- */
interface Center {
  col: number;
  row: number;
  x: number;
  y: number;
}

function gridCenters(
  width: number,
  height: number,
  spacing: number,
  angDeg: number,
  gridType: GridType,
): Center[] {
  const centers: Center[] = [];
  if (gridType === 'radial') {
    const cx = width / 2;
    const cy = height / 2;
    const maxR = Math.hypot(cx, cy);
    const base = ((angDeg || 0) * Math.PI) / 180;
    centers.push({ col: 0, row: -1, x: cx, y: cy });
    let ring = 0;
    for (let r = spacing; r <= maxR; r += spacing, ring++) {
      const n = Math.max(1, Math.round((2 * Math.PI * r) / spacing));
      for (let k = 0; k < n; k++) {
        const a = base + (k / n) * Math.PI * 2 + (ring % 2) * (Math.PI / n);
        const x = cx + r * Math.cos(a);
        const y = cy + r * Math.sin(a);
        if (x < 0 || x >= width || y < 0 || y >= height) continue;
        centers.push({ col: k, row: ring, x, y });
      }
    }
    return centers;
  }
  const ang = ((angDeg || 0) * Math.PI) / 180;
  const cos = Math.cos(ang);
  const sin = Math.sin(ang);
  const yStep = gridType === 'hex' ? spacing * 0.866 : spacing;
  const xStaggered = gridType === 'hex';
  if (!ang) {
    let row = 0;
    for (let y = 0; y <= height + yStep; y += yStep, row++) {
      const xoff = xStaggered ? ((row % 2) * spacing) / 2 : 0;
      for (let x = xoff; x <= width; x += spacing) {
        if (x < 0 || x > width || y < 0 || y > height) continue;
        centers.push({ col: Math.round(x / spacing), row, x, y });
      }
    }
    return centers;
  }
  const cs = [
    [0, 0],
    [width, 0],
    [0, height],
    [width, height],
  ].map(([x, y]) => ({ u: (x ?? 0) * cos + (y ?? 0) * sin, v: -(x ?? 0) * sin + (y ?? 0) * cos }));
  const uMin = Math.min(...cs.map((c) => c.u));
  const uMax = Math.max(...cs.map((c) => c.u));
  const vMin = Math.min(...cs.map((c) => c.v));
  const vMax = Math.max(...cs.map((c) => c.v));
  let row = 0;
  for (let v = vMin; v < vMax; v += yStep, row++) {
    const xoff = xStaggered ? ((row % 2) * spacing) / 2 : spacing / 2;
    for (let u = uMin + xoff; u < uMax; u += spacing) {
      const cv = v + spacing / 2;
      const x = u * cos - cv * sin;
      const y = u * sin + cv * cos;
      if (x < 0 || x >= width || y < 0 || y >= height) continue;
      centers.push({ col: Math.round((u - uMin) / spacing), row, x, y });
    }
  }
  return centers;
}

/* ---- CORE: walk the grid, build dots ---- */
export function buildDots(cfg: DotRenderConfig, density: (x: number, y: number) => number): Dot[] {
  const {
    width,
    height,
    spacing,
    minFrac,
    maxFrac,
    intensity,
    shape,
    seed,
    seamless,
    markShape,
    jitterDots = true,
  } = cfg;
  const loci = buildLoci(cfg);
  const dots: Dot[] = [];
  const half = spacing / 2;
  const centers = gridCenters(width, height, spacing, cfg.gridAngle, cfg.gridType);
  let shineFn: ((x: number, y: number) => number) | null = null;
  if (cfg.shine) {
    const a = ((cfg.shineAngle || 135) * Math.PI) / 180;
    const dx = Math.cos(a);
    const dy = Math.sin(a);
    const projs = [
      [0, 0],
      [width, 0],
      [0, height],
      [width, height],
    ].map(([X, Y]) => (X ?? 0) * dx + (Y ?? 0) * dy);
    const pmin = Math.min(...projs);
    const span = Math.max(...projs) - pmin || 1;
    shineFn = (x: number, y: number): number => 1 - ((x * dx + y * dy - pmin) / span) * 0.92;
  }
  for (const cen of centers) {
    const { col, row, x: gx, y: gy } = cen;
    const rand = cellRng(col, row, seed);
    const jf = seamless || !jitterDots ? 0 : 0.16 * Math.max(0, 1 - maxFrac);
    const rx = gx + (rand() - 0.5) * spacing * jf;
    const ry = gy + (rand() - 0.5) * spacing * jf;
    let d = density(rx, ry);
    if (loci) {
      let t = 0;
      for (const o of loci) t += lociBand(o, rx, ry, markShape, cfg.markMask) * o.sign * o.strength;
      d = clamp(d + t, 0, 1);
    }
    if (cfg.fadeMode && cfg.fadeMode !== 'none') {
      const keep = fadeMul(rx, ry, cfg);
      if (rand() > keep) continue;
    }
    const visible = d * intensity;
    const baseV = cfg.baseFill || 0;
    const culled =
      (cfg.coverage && visible < cfg.coverage * 0.85) ||
      (visible < 0.045 && rand() > visible * 2.4);
    let radius: number;
    let alpha: number;
    if (culled) {
      if (baseV <= 0) continue;
      radius = (0.12 + baseV * 0.5) * half * 0.98;
      alpha = clamp(0.1 + baseV * 0.8, 0.04, 0.95);
    } else {
      const shaped = cfg.shapePow ? Math.max(visible, 0) ** cfg.shapePow : visible;
      radius = Math.max(0.1, (minFrac + (maxFrac - minFrac) * shaped) * half * 0.98);
      if (baseV > 0) {
        const gr = (0.12 + baseV * 0.5) * half * 0.98;
        if (radius < gr) radius = gr;
      }
      alpha = clamp(0.18 + visible * 0.82, 0, 1);
    }
    if (radius < 0.25) continue;
    if (shineFn) alpha *= clamp(shineFn(rx, ry), 0.05, 1);
    if (alpha < 0.02) continue;
    const shp = shape === 'mixed' ? 'mixed' : shape;
    const shift = rand();
    const color = cfg.colorAt ? cfg.colorAt(rx, ry) : pickColor(cfg.colors, cfg.derived, shift);
    const dot: Dot = {
      x: rx,
      y: ry,
      r: radius,
      color,
      alpha,
      shape: shp,
      sw: cfg.strokeWeight ?? 1,
      tr: 0.45,
    };
    if (shp === 'conv') {
      let mv: MarkVariant = cfg.markVariant || 'axis';
      if (mv === 'mix') mv = rand() < 0.5 ? 'axis' : 'diag';
      dot.mk = { variant: mv };
    }
    dots.push(dot);
  }
  return dots;
}

export function drawDots(ctx: Ctx2D, dots: Dot[], opacityMul: number = 1): void {
  for (const d of dots) {
    ctx.globalAlpha = d.alpha * opacityMul;
    ctx.fillStyle = d.color;
    ctx.strokeStyle = d.color;
    const s = resolveShape(d.shape, d.x, d.y);
    const r = d.r;
    const mul = d.sw ?? 1;
    if (s === 'line') {
      ctx.lineWidth = r * 0.55 * mul;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(d.x, d.y - r * 2.4);
      ctx.lineTo(d.x, d.y + r * 2.4);
      ctx.stroke();
    } else if (s === 'dash') {
      ctx.lineWidth = r * 0.55 * mul;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(d.x - r * 2.4, d.y);
      ctx.lineTo(d.x + r * 2.4, d.y);
      ctx.stroke();
    } else if (s === 'square') {
      const sz = r * 1.7;
      const rr = r * 0.28;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(d.x - sz / 2, d.y - sz / 2, sz, sz, rr);
      else ctx.rect(d.x - sz / 2, d.y - sz / 2, sz, sz);
      ctx.fill();
    } else if (s === 'triangle') {
      const h = r * 2;
      const w = r * 1.9;
      ctx.beginPath();
      ctx.moveTo(d.x, d.y - h * 0.6);
      ctx.lineTo(d.x - w / 2, d.y + h * 0.4);
      ctx.lineTo(d.x + w / 2, d.y + h * 0.4);
      ctx.closePath();
      ctx.fill();
    } else if (s === 'slasho') {
      const sw = Math.max(0.4, r * 0.32);
      const rr = r * 0.92;
      const k = r * 0.78;
      ctx.lineWidth = sw;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(d.x, d.y, rr, 0, Math.PI * 2);
      ctx.moveTo(d.x - k, d.y + k);
      ctx.lineTo(d.x + k, d.y - k);
      ctx.stroke();
    } else if (s === 'conv') {
      const v: 'axis' | 'diag' = d.mk?.variant === 'diag' ? 'diag' : 'axis';
      let p2d = MARK_P2D[v];
      if (!p2d) {
        p2d = new Path2D(MARK_PATHS[v]);
        MARK_P2D[v] = p2d;
      }
      const sc = r * 0.05;
      ctx.save();
      ctx.translate(d.x, d.y);
      ctx.scale(sc, sc);
      ctx.translate(-20, -20);
      ctx.fill(p2d);
      ctx.restore();
    } else if (s === 'chevron') {
      ctx.lineWidth = Math.max(0.4, r * 0.46 * mul);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(d.x - r, d.y - r * 0.45);
      ctx.lineTo(d.x, d.y + r * 0.45);
      ctx.lineTo(d.x + r, d.y - r * 0.45);
      ctx.stroke();
    } else if (s === 'triUp' || s === 'triDown' || s === 'tri') {
      const pts =
        s === 'tri'
          ? [
              { x: d.x - r, y: d.y - r * 0.8 },
              { x: d.x + r, y: d.y - r * 0.8 },
              { x: d.x, y: d.y + r * 1.6 },
            ]
          : triVerts(d.x, d.y, r, s === 'triUp');
      const rad = r * (d.tr ?? 0.2);
      const n = pts.length;
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const p1 = pts[i];
        const p0 = pts[(i - 1 + n) % n];
        const p2 = pts[(i + 1) % n];
        if (!p1 || !p0 || !p2) continue;
        const u1 = Math.hypot(p0.x - p1.x, p0.y - p1.y) || 1;
        const u2 = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
        const ax = p1.x + ((p0.x - p1.x) / u1) * rad;
        const ay = p1.y + ((p0.y - p1.y) / u1) * rad;
        const bx = p1.x + ((p2.x - p1.x) / u2) * rad;
        const by = p1.y + ((p2.y - p1.y) / u2) * rad;
        if (i) ctx.lineTo(ax, ay);
        else ctx.moveTo(ax, ay);
        ctx.quadraticCurveTo(p1.x, p1.y, bx, by);
      }
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.arc(d.x, d.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

export function drawCanvas(ctx: Ctx2D, dots: Dot[], cfg: DotRenderConfig): void {
  const { width, height, background, backgroundMode } = cfg;
  ctx.clearRect(0, 0, width, height);
  if (backgroundMode !== 'transparent') {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
  }
  drawDots(ctx, dots, 1);
}

export type { DerivedSeed };
